// Servicio API para incidencias/tickets de soporte
import { api } from './api';
import type {
  Incidencia,
  IncidenciaCreatePayload,
  IncidenciaDashboardStats,
  IncidenciaFilters,
  IncidenciaHistorial,
  IncidenciaListResponse,
  IncidenciaMensaje,
  IncidenciaMensajeCreatePayload,
  IncidenciaUpdatePayload,
  IncidenciaWithRelations,
} from '@/types/incidencia';

// === CRM Endpoints ===

export const incidenciaService = {
  /** Lista incidencias con filtros y paginación */
  async list(filters: IncidenciaFilters = {}): Promise<IncidenciaListResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    const { data } = await api.get<IncidenciaListResponse>(
      `/incidencias?${params.toString()}`
    );
    return data;
  },

  /** Obtiene una incidencia por ID */
  async getById(id: string): Promise<IncidenciaWithRelations> {
    const { data } = await api.get<IncidenciaWithRelations>(`/incidencias/${id}`);
    return data;
  },

  /** Crea una incidencia (CRM, en nombre del cliente) */
  async create(payload: IncidenciaCreatePayload): Promise<Incidencia> {
    const { data } = await api.post<Incidencia>('/incidencias', payload);
    return data;
  },

  /** Actualiza una incidencia */
  async update(id: string, payload: IncidenciaUpdatePayload): Promise<Incidencia> {
    const { data } = await api.put<Incidencia>(`/incidencias/${id}`, payload);
    return data;
  },

  /** Obtiene mensajes de una incidencia (incluye notas internas) */
  async getMessages(id: string): Promise<IncidenciaMensaje[]> {
    const { data } = await api.get<IncidenciaMensaje[]>(
      `/incidencias/${id}/messages`
    );
    return data;
  },

  /** Agrega mensaje o nota interna */
  async addMessage(
    id: string,
    payload: IncidenciaMensajeCreatePayload
  ): Promise<IncidenciaMensaje> {
    const { data } = await api.post<IncidenciaMensaje>(
      `/incidencias/${id}/messages`,
      payload
    );
    return data;
  },

  /** Asigna una incidencia a un agente */
  async assign(id: string, assignedToId: string): Promise<Incidencia> {
    const { data } = await api.post<Incidencia>(
      `/incidencias/${id}/assign?assigned_to_id=${assignedToId}`
    );
    return data;
  },

  /** Cierra una incidencia */
  async close(id: string): Promise<Incidencia> {
    const { data } = await api.post<Incidencia>(`/incidencias/${id}/close`);
    return data;
  },

  /** Obtiene historial de cambios */
  async getHistorial(id: string): Promise<IncidenciaHistorial[]> {
    const { data } = await api.get<IncidenciaHistorial[]>(
      `/incidencias/${id}/historial`
    );
    return data;
  },

  /** Dashboard stats */
  async getDashboardStats(
    assignedToId?: string
  ): Promise<IncidenciaDashboardStats> {
    const params = assignedToId ? `?assigned_to_id=${assignedToId}` : '';
    const { data } = await api.get<IncidenciaDashboardStats>(
      `/incidencias/dashboard${params}`
    );
    return data;
  },

  /** Incidencias por expediente */
  async getByExpediente(expedienteId: string): Promise<Incidencia[]> {
    const { data } = await api.get<Incidencia[]>(
      `/incidencias/by-expediente/${expedienteId}`
    );
    return data;
  },

  /** Flag de cliente: conteo de incidencias abiertas */
  async getClientFlag(
    userId: string
  ): Promise<{ user_id: string; open_incidencias_count: number }> {
    const { data } = await api.get<{
      user_id: string;
      open_incidencias_count: number;
    }>(`/incidencias/client-flag/${userId}`);
    return data;
  },
};

// === Portal Cliente Endpoints ===

export const clienteIncidenciaService = {
  /** Mis incidencias */
  async list(estado?: string): Promise<Incidencia[]> {
    const params = estado ? `?estado=${estado}` : '';
    const { data } = await api.get<Incidencia[]>(
      `/cliente/incidencias${params}`
    );
    return data;
  },

  /** Crear incidencia */
  async create(payload: IncidenciaCreatePayload): Promise<Incidencia> {
    const { data } = await api.post<Incidencia>(
      '/cliente/incidencias',
      payload
    );
    return data;
  },

  /** Ver mi incidencia */
  async getById(id: string): Promise<Incidencia> {
    const { data } = await api.get<Incidencia>(`/cliente/incidencias/${id}`);
    return data;
  },

  /** Mensajes de mi incidencia (sin notas internas) */
  async getMessages(id: string): Promise<IncidenciaMensaje[]> {
    const { data } = await api.get<IncidenciaMensaje[]>(
      `/cliente/incidencias/${id}/messages`
    );
    return data;
  },

  /** Agregar mensaje */
  async addMessage(
    id: string,
    payload: IncidenciaMensajeCreatePayload
  ): Promise<IncidenciaMensaje> {
    const { data } = await api.post<IncidenciaMensaje>(
      `/cliente/incidencias/${id}/messages`,
      payload
    );
    return data;
  },
};
